import { api, ensureCsrfCookie } from '@/api/client';
import type { CursorPage } from '@/api/commercial';
import type { QuotationData } from '@/types/generated';

/**
 * Quotations.
 *
 * EVERY STATE CHANGE IS ITS OWN CALL, mirroring the API. There is deliberately no
 * `updateQuotation({ status })`: issuing supersedes earlier revisions and moves the
 * RFQ, accepting wins the work, rejecting needs a reason. A single PATCH would make
 * those look like field assignments and invite a client to attempt two at once.
 *
 * The response types are GENERATED from the PHP DTOs, so this module cannot drift
 * from the wire.
 */

export interface QuotationFilters {
  search?: string | undefined;
  status?: string | undefined;
  customer_id?: string | undefined;
  openOnly?: boolean | undefined;
  /**
   * Off by default, matching the server: a list showing v1, v2 and v3 of one
   * negotiation as three rows reads as three opportunities.
   */
  allRevisions?: boolean | undefined;
  cursor?: string | null | undefined;
}

export async function fetchQuotations(
  filters: QuotationFilters = {},
): Promise<CursorPage<QuotationData>> {
  const { data } = await api.get<CursorPage<QuotationData>>('/quotations', {
    params: pruned({
      search: filters.search,
      status: filters.status,
      customer_id: filters.customer_id,
      open_only: filters.openOnly ? 1 : undefined,
      current_only: filters.allRevisions ? 0 : undefined,
      cursor: filters.cursor ?? undefined,
    }),
  });
  return data;
}

export async function fetchQuotation(id: string): Promise<QuotationData> {
  const { data } = await api.get<{ quotation: QuotationData }>(`/quotations/${id}`);
  return data.quotation;
}

/**
 * Price an RFQ.
 *
 * Called again for the same RFQ this returns a REVISION, not a replacement — which is
 * what "reprice after the customer pushed back" means.
 */
export async function priceRfq(rfqId: string, asOf?: string): Promise<QuotationData> {
  await ensureCsrfCookie();
  const { data } = await api.post<{ quotation: QuotationData }>('/quotations', {
    rfq_id: rfqId,
    as_of: asOf,
  });
  return data.quotation;
}

export interface AmendQuotationPayload {
  valid_until?: string | null | undefined;
  terms?: string | null | undefined;
  notes?: string | null | undefined;
  estimated_cost_minor?: number | null | undefined;
}

export async function amendQuotation(
  id: string,
  payload: AmendQuotationPayload,
): Promise<QuotationData> {
  await ensureCsrfCookie();
  const { data } = await api.patch<{ quotation: QuotationData }>(`/quotations/${id}`, payload);
  return data.quotation;
}

/**
 * Override the price.
 *
 * `priceMinor: null` clears the override and restores the calculated price; a reason is
 * required only when setting one, because reverting is not itself a priced decision.
 */
export async function overrideQuotationPrice(
  id: string,
  priceMinor: number | null,
  reason?: string,
): Promise<QuotationData> {
  await ensureCsrfCookie();
  const { data } = await api.post<{ quotation: QuotationData }>(`/quotations/${id}/override`, {
    price_minor: priceMinor,
    reason: priceMinor === null ? null : reason,
  });
  return data.quotation;
}

export async function issueQuotation(id: string): Promise<QuotationData> {
  await ensureCsrfCookie();
  const { data } = await api.post<{ quotation: QuotationData }>(`/quotations/${id}/issue`);
  return data.quotation;
}

/**
 * Record the customer's answer — the Phase 3 training label.
 *
 * Only `accepted` and `rejected`: expiry and supersession are consequences, not answers.
 */
export async function recordQuotationOutcome(
  id: string,
  outcome: 'accepted' | 'rejected',
  reason?: string,
): Promise<QuotationData> {
  await ensureCsrfCookie();
  const { data } = await api.post<{ quotation: QuotationData }>(`/quotations/${id}/outcome`, {
    outcome,
    reason,
  });
  return data.quotation;
}

export async function deleteQuotation(id: string): Promise<void> {
  await ensureCsrfCookie();
  await api.delete(`/quotations/${id}`);
}

/**
 * Download the document.
 *
 * Fetched as a blob rather than opened as a URL, because the endpoint STREAMS from
 * private storage behind the policy check — there is no signed link to point a new tab
 * at, deliberately (a signed URL is a bearer credential that survives being pasted into
 * a chat).
 *
 * Returns null on a 202: the render is queued, so "not yet" is a real and temporary
 * answer that the caller should retry rather than treat as an error.
 */
export async function downloadQuotationPdf(id: string): Promise<Blob | null> {
  const response = await api.get(`/quotations/${id}/pdf`, {
    responseType: 'blob',
    // 202 carries a JSON body, not a PDF, so it must not be treated as a failure.
    validateStatus: (status) => status === 200 || status === 202,
  });

  return response.status === 202 ? null : (response.data as Blob);
}

function pruned(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== '' && value !== null,
    ),
  );
}
